import { type FunctionComponent, useEffect, useMemo } from "react";
import { useController, useFormContext } from "react-hook-form";

import { useInputDisabled, useInputReadonly } from "../../InputGroup";
import { SubmitGroup } from "../../SubmitGroup";
import { ValidityGroup } from "../../ValidityGroup";
import { required as requiredRule } from "../../validation/rules";
import type { RTEFieldDefs } from "../RichTextEditorProps";
import { Input } from "./Input";

/**
 * @documental
 * @export Field
 * @name
 * RichTextEditor.Field
 */
export const Field: FunctionComponent<RTEFieldDefs> = (props) => {
  const { name, required = false, defaultValue = null, validator, ...inputDefs } = props;
  const disabled = useInputDisabled(props);
  const readOnly = useInputReadonly(props);
  const { resetField } = useFormContext();
  const rules = useMemo(
    () => ({
      ...(disabled || readOnly ? requiredRule : requiredRule(required)),
      validate: disabled || readOnly ? undefined : validator,
    }),
    [disabled, readOnly, required, validator],
  );

  useEffect(() => {
    if (disabled) resetField(name);
  }, [disabled, name, resetField]);

  const { field, fieldState, formState } = useController({ name, defaultValue, rules });
  const { ref, value, onChange, onBlur } = field;
  const { invalid, error } = fieldState;
  const { isSubmitting } = formState;
  return (
    <SubmitGroup submitting={isSubmitting}>
      <ValidityGroup invalid={invalid} error={error?.message}>
        <Input
          {...inputDefs}
          defaultValue={defaultValue}
          onChange={onChange}
          onFocusExit={onBlur}
          ref={ref}
          required={required}
          value={value}
        />
      </ValidityGroup>
    </SubmitGroup>
  );
};
